/**
 * `LagLineChart` — hand-built SVG line/area chart of freshness lag over time,
 * rendered inside `LagDetailModal` as the "graph" alternative to the bar view.
 *
 * Why hand-built (no charting lib): a single evenly-spaced series in a modal
 * needs only a `<line>`/`<path>` per the model from `lib/lag-series.ts`. That
 * keeps the bundle flat, inherits the `.dark` theme + the `ok/warn/down`
 * tokens for free, and guarantees the line matches the bar's color grammar.
 *
 * All geometry/severity is computed by `buildChartModel` (pure, tested). This
 * component only maps that model to SVG and owns the hover interaction.
 *
 * Encoding: per-segment line color by `lagToTone`; a soft single-tint area
 * fill under the line (broken at gaps); an ABSOLUTE seconds y-axis (the graph's
 * edge over the relative-scale bars); `null` buckets break the line and leave a
 * faint baseline tick (gap ≠ 0-lag). No SLA threshold line (DESIGN.md §6.1).
 */
import { motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  buildChartModel,
  bucketsToSeries,
  DEFAULT_CHART_DIMS,
  type LagMetric,
} from '../lib/lag-series';
import { lagToTone, type LagTone, type Pipeline } from '../lib/lag-bars';
import { formatLagTimeRange, type LagWindow } from '../lib/lag-time';
import { formatLag } from './IndexerPipelineCard';
import { HoverTooltip } from './HoverTooltip';
import { LagBucketTip, type LagBucketView } from './LagHistoryBar';

const STROKE: Record<LagTone, string> = {
  ok: 'stroke-ok',
  warn: 'stroke-warn',
  down: 'stroke-down',
  none: 'stroke-line-strong',
};

const FILL: Record<LagTone, string> = {
  ok: 'fill-ok',
  warn: 'fill-warn',
  down: 'fill-down',
  none: 'fill-line-strong',
};

const CHART_HEIGHT = DEFAULT_CHART_DIMS.height;

export interface LagLineChartProps {
  buckets: readonly LagBucketView[];
  pipeline: Pipeline;
  windowSel: LagWindow;
  metric?: LagMetric;
  endpointKey: string;
}

/** Plain `0` for the baseline tick; `formatLag` (e.g. `0.4s`, `2s`) above. */
function axisLabel(v: number): string {
  return v === 0 ? '0' : formatLag(v);
}

const TONE_WORD: Record<LagTone, string> = {
  ok: 'operational',
  warn: 'degraded',
  down: 'outage',
  none: 'no data',
};

/**
 * Plain-text equivalent of the visual `LagBucketTip`, for the keyboard/SR
 * live region. Mirrors the same tone + value branches the tooltip renders so
 * assistive-tech users hear exactly what sighted hover shows.
 */
function bucketSpokenSummary(
  bucket: LagBucketView,
  pipeline: Pipeline,
  windowSel: LagWindow,
  metric: LagMetric,
): string {
  const downCount = bucket.downCount ?? 0;
  const warnCount = bucket.warnCount ?? 0;
  const totalProbes = bucket.totalProbes ?? 0;
  const failureOverride: LagTone | null =
    downCount > 0 ? 'down' : warnCount > 0 ? 'warn' : null;
  const tone: LagTone = failureOverride ?? lagToTone(bucket.lagP95Sec, pipeline);
  const range = formatLagTimeRange(bucket, windowSel);
  const head = `${TONE_WORD[tone]}, ${range}`;

  if (totalProbes === 0 && bucket.sampleCount === 0) {
    return head;
  }
  if (bucket.sampleCount === 0 && (downCount > 0 || warnCount > 0)) {
    const parts: string[] = [];
    if (downCount > 0) parts.push(`${String(downCount)} down`);
    if (warnCount > 0) parts.push(`${String(warnCount)} warn`);
    parts.push('lag not measured');
    return `${head}, ${parts.join(', ')}`;
  }
  const value = metric === 'p95' ? bucket.lagP95Sec : bucket.lagMaxSec;
  const valueLabel = value === null ? '—' : formatLag(value);
  return `${head}, ${metric} ${valueLabel}, ${String(bucket.sampleCount)} samples`;
}

/**
 * Aggregate headline for the chart's `aria-label`, so non-visual users get
 * the signal without stepping through every point. Mirrors `LagHistoryBar`'s
 * enrichment for parity.
 */
function chartHeadline(
  buckets: readonly LagBucketView[],
  endpointKey: string,
  windowSel: LagWindow,
  metric: LagMetric,
): string {
  let peak: number | null = null;
  let down = 0;
  let warn = 0;
  for (const b of buckets) {
    const v = metric === 'p95' ? b.lagP95Sec : b.lagMaxSec;
    if (v !== null && (peak === null || v > peak)) peak = v;
    if ((b.downCount ?? 0) > 0) down += 1;
    if ((b.warnCount ?? 0) > 0) warn += 1;
  }
  const peakLabel = peak === null ? '—' : formatLag(peak);
  return `${windowSel} ${metric} lag chart for ${endpointKey}: peak ${peakLabel}, ${String(down)} down, ${String(warn)} warn. Use left and right arrow keys to step through buckets.`;
}

export function LagLineChart({
  buckets,
  pipeline,
  windowSel,
  metric = 'p95',
  endpointKey,
}: LagLineChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_CHART_DIMS.width);
  const [hover, setHover] = useState<{ idx: number } | null>(null);

  // Seed width from the real DOM before first paint so the memoized model's
  // geometry matches the rendered width before any onMove can fire (the
  // ResizeObserver below only corrects subsequent resizes, one frame late).
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (el) {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    }
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  const model = useMemo(() => {
    const series = bucketsToSeries(buckets, metric, pipeline);
    return buildChartModel(series, windowSel, {
      ...DEFAULT_CHART_DIMS,
      width,
    });
  }, [buckets, metric, pipeline, windowSel, width]);

  const n = buckets.length;
  const { dims, baselineY } = model;

  if (n === 0) {
    return (
      <div
        ref={wrapRef}
        className="flex items-center justify-center text-[12px] text-ink-3"
        style={{ height: CHART_HEIGHT }}
      >
        No lag history for this window.
      </div>
    );
  }

  const hoveredIdx = hover?.idx ?? null;
  const hoveredCoord = hoveredIdx !== null ? model.coords[hoveredIdx] : null;
  const hoveredBucket = hoveredIdx !== null ? buckets[hoveredIdx] : null;

  const innerW = dims.width - dims.padLeft - dims.padRight;

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = innerW <= 0 ? 0 : (x - dims.padLeft) / innerW;
    const idx = Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1))));
    setHover((prev) => (prev && prev.idx === idx ? prev : { idx }));
  };

  // Keyboard parity with mouse hover: arrows step the active point, Home/End
  // jump to the ends, Escape clears. The first arrow lands on the last bucket
  // (most recent) so a sighted-vs-SR read starts from "now".
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const step = e.key === 'ArrowRight' ? 1 : -1;
      setHover((prev) => {
        const cur = prev?.idx ?? n - 1;
        const next = Math.max(0, Math.min(n - 1, cur + step));
        return prev?.idx === next ? prev : { idx: next };
      });
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHover({ idx: 0 });
    } else if (e.key === 'End') {
      e.preventDefault();
      setHover({ idx: n - 1 });
    } else if (e.key === 'Escape') {
      if (hover) setHover(null);
    }
  };

  const liveSummary =
    hoveredBucket !== null
      ? bucketSpokenSummary(hoveredBucket, pipeline, windowSel, metric)
      : '';

  // Hovered point geometry in SVG (== px) coords; falls back to the baseline
  // for gap buckets so the guide line still tracks the cursor.
  const svgX =
    hoveredIdx === null
      ? 0
      : (hoveredCoord?.x ??
        (n <= 1
          ? dims.padLeft + innerW / 2
          : dims.padLeft + (hoveredIdx / (n - 1)) * innerW));
  const svgY = hoveredCoord?.y ?? baselineY;

  const rect = wrapRef.current?.getBoundingClientRect();
  const tipX = rect ? rect.left + svgX : 0;
  const tipY = rect ? rect.top + svgY : 0;

  return (
    // Custom interactive data-viz widget: a focusable chart surface that mirrors
    // mouse hover with arrow/Home/End/Escape keys. role="group" + tabIndex is the
    // intentional pattern here; jsx-a11y's non-interactive rules don't model a
    // keyboard-operable chart, so they're disabled for this element only.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex
    <div tabIndex={0}
      ref={wrapRef}
      className="relative w-full rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ton-cyan/40"
      role="group"
      aria-label={chartHeadline(buckets, endpointKey, windowSel, metric)}
      onMouseMove={onMove}
      onMouseLeave={() => {
        setHover(null);
      }}
      onKeyDown={onKeyDown}
      onBlur={() => {
        setHover(null);
      }}
    >
      {/* Purely-visual graphic: the wrapping role="group" carries the
          accessible headline and the aria-live region announces each point,
          so the SVG is hidden from AT to avoid a redundant double-read. */}
      <svg
        width={width}
        height={CHART_HEIGHT}
        viewBox={`0 0 ${String(width)} ${String(CHART_HEIGHT)}`}
        aria-hidden="true"
        className="block"
      >
        {/* y-axis gridlines + labels */}
        {model.ticks.map((t) => (
          <g key={`tick-${String(t.v)}`}>
            <line
              x1={dims.padLeft}
              x2={dims.width - dims.padRight}
              y1={t.y}
              y2={t.y}
              className="stroke-line"
              strokeWidth={1}
              strokeDasharray={t.v === 0 ? undefined : '2 3'}
            />
            <text
              x={dims.padLeft - 6}
              y={t.y + 3}
              textAnchor="end"
              className="fill-ink-3 font-mono text-[9.5px] [font-feature-settings:'tnum']"
            >
              {axisLabel(t.v)}
            </text>
          </g>
        ))}

        <motion.g
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* per-range area fill — red only under red ranges, not the whole chart */}
          {model.areaSegments.map((a, i) => (
            <path
              key={`area-${String(i)}`}
              d={a.d}
              className={FILL[a.tone]}
              fillOpacity={0.14}
            />
          ))}
          {/* per-segment colored line */}
          {model.lineSegments.map((s, i) => (
            <line
              key={`seg-${String(i)}`}
              x1={s.x1}
              y1={s.y1}
              x2={s.x2}
              y2={s.y2}
              className={STROKE[s.tone]}
              strokeWidth={2}
              strokeLinecap="round"
            />
          ))}
          {/* warn/down spike markers */}
          {model.markers.map((mk, i) => (
            <circle
              key={`mk-${String(i)}`}
              cx={mk.x}
              cy={mk.y}
              r={2.5}
              className={FILL[mk.tone]}
            />
          ))}
        </motion.g>

        {/* x-axis time labels */}
        {model.xLabels.map((l, i) => (
          <text
            key={`xl-${String(i)}`}
            x={l.x}
            y={CHART_HEIGHT - 6}
            textAnchor={
              i === 0
                ? 'start'
                : i === model.xLabels.length - 1
                  ? 'end'
                  : 'middle'
            }
            className="fill-ink-3 font-mono text-[9.5px] [font-feature-settings:'tnum']"
          >
            {l.label}
          </text>
        ))}

        {/* hover guide + cursor point */}
        {hoveredIdx !== null && (
          <line
            x1={svgX}
            x2={svgX}
            y1={dims.padTop}
            y2={baselineY}
            className="stroke-line-strong"
            strokeWidth={1}
          />
        )}
        {hoveredCoord && (
          <circle
            cx={hoveredCoord.x}
            cy={hoveredCoord.y}
            r={3.5}
            className="fill-paper-1 stroke-ink-1"
            strokeWidth={1.5}
          />
        )}
      </svg>

      {hover && hoveredBucket && (
        <HoverTooltip x={tipX} y={tipY}>
          <LagBucketTip
            bucket={hoveredBucket}
            pipeline={pipeline}
            windowSel={windowSel}
          />
        </HoverTooltip>
      )}

      {/* Keyboard/SR readout of the active point — mirrors LagBucketTip in
          plain text so non-pointer users get the same per-bucket detail. */}
      <div className="sr-only" aria-live="polite">
        {liveSummary}
      </div>
    </div>
  );
}
